<template>
    <div class="flex flex-col w-full p-5 gap-3 max-w-5xl">
        <app-separator :label="$t('organizations.currentOffer')"></app-separator>
        <table class="text-xs table-fixed">
            <template v-if="!!currentSubscription">
                <thead class="hidden">
                    <tr>
                        <th scope="col"></th>
                        <th scope="col" style="width: 22em"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td colspan="2" class="border p-1">
                            <div class="flex items-baseline flex-col">
                                <span>
                                    <span class="text-lg font-bold">
                                        {{ $t('organizations.offers.' + currentSubscription.type) }}
                                    </span>
                                    <span>
                                        {{ currentSubscription.startDate | humanizeDate }} -
                                        {{ currentSubscription.endDate | humanizeDate }}
                                    </span>
                                </span>
                                <span>{{ $t('organizations.code') }} : {{ currentSubscription.code }}</span>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td class="p-1 border text-center">
                            <div class="w-full">
                                <span class="text-lg font-bold w-full">
                                    {{ currentSubscription.remainingCredits }} {{ $t('organizations.remainingCredit') }}
                                </span>
                            </div>
                            <p>
                                <progress
                                    id="file"
                                    :max="currentSubscription.creditAmount"
                                    :value="currentSubscription.remainingCredits"
                                >
                                    {{
                                        $n(
                                            (currentSubscription.remainingCredits / currentSubscription.creditAmount) *
                                                100,
                                            integerFormat,
                                        )
                                    }}
                                </progress>
                            </p>
                            <span>
                                {{
                                    $t('organizations.remainingTips', {
                                        percent: $n(
                                            (currentSubscription.remainingCredits / currentSubscription.creditAmount) *
                                                100,
                                            integerFormat,
                                        ),
                                        creditAmount: currentSubscription.creditAmount,
                                    })
                                }}
                            </span>
                        </td>
                        <td class="p-1 border">
                            <span class="text-lg font-bold">
                                {{ $t('organizations.frequencies.' + currentSubscription.billingFrequency) }}
                            </span>
                            <br />
                            <span v-if="nextLevy">
                                {{ $t('organization.nextLevy') }} {{ nextLevy.date }} :
                                {{ $n(nextLevy.amount, 'currency') }}
                            </span>
                            <br />
                            <div class="flex gap-2">
                                <p v-if="currentSubscription.rum">
                                    <b>{{ $t('organizations.sepaRum') }} :</b>
                                    {{ currentSubscription.rum }}
                                </p>
                                <p v-if="currentSubscription.iban">
                                    <b>IBAN :</b>
                                    {{ currentSubscription.iban.substring(0, 4) }} [...]
                                    {{ currentSubscription.iban.substring(-4) }}
                                </p>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </template>
            <template v-else>
                <tbody>
                    <tr>
                        <td colspan="2" class="py-3">{{ $t('organizations.commonFeatures') }}</td>
                    </tr>
                    <tr class="border">
                        <td class="p-1" style="width: 3rem">
                            <div class="flex flex-col">
                                <input type="radio" checked :value="true" @input="$event.value = true" />
                            </div>
                        </td>
                        <td class="p-1">
                            <div class="flex flex-col">
                                <span class="text-lg font-bold">
                                    {{ $t('organizations.offers.starter') }}
                                </span>
                                <span class="text-sm text-gray-500">
                                    {{ $t('organizations.starterTips') }}
                                </span>
                            </div>
                        </td>
                    </tr>
                    <tr class="border">
                        <td class="p-1 pt-3" @click="animateCTA()">
                            <div class="flex flex-col">
                                <input type="radio" :value="false" @click.prevent="" />
                            </div>
                        </td>
                        <td class="p-1 pt-3" @click="animateCTA()">
                            <div class="flex flex-col">
                                <span class="text-lg font-bold">
                                    {{ $t('organizations.offers.pro') }}
                                </span>
                                <span class="text-sm text-gray-500">
                                    {{ $t('organizations.proTips') }}
                                </span>
                                <span>
                                    <span class="text-sm text-gray-500 font-bold">
                                        {{ $t('organizations.proTips2') }}
                                    </span>
                                    <span class="text-xs text-gray-500">({{ $t('organizations.proTips3') }})</span>
                                </span>
                            </div>
                        </td>
                    </tr>
                    <tr v-if="!nextOffer">
                        <td colspan="2">
                            <div class="w-full flex justify-start py-3">
                                <app-button
                                    @click="sendProMail()"
                                    variant="primary"
                                    :class="buttonCTAClasses"
                                    :label="$t('organizations.askForPro')"
                                ></app-button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </template>
        </table>
        <template v-if="nextOffer">
            <app-separator :label="$t('organizations.nextOffer')"></app-separator>
            <table class="text-xs table-fixed">
                <thead class="hidden">
                    <tr>
                        <th scope="col"></th>
                        <th scope="col" style="width: 22em"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td colspan="2" class="border p-1">
                            <div class="flex justify-between items-center">
                                <div class="flex items-baseline flex-col">
                                    <span>
                                        <span class="text-lg font-bold">
                                            {{ $t('organizations.offers.' + nextOffer.type) }}
                                        </span>

                                        <span>
                                            {{ nextOffer.startDate | humanizeDate }} -
                                            {{ nextOffer.endDate | humanizeDate }}
                                        </span>
                                    </span>
                                    <span>{{ $t('organizations.code') }} : {{ nextOffer.code }}</span>
                                </div>
                                <span class="text-lg font-bold">
                                    <span>{{ $t('organizations.offerStatus.title') }} :</span>
                                    <span
                                        :class="{
                                            'text-yellow-500': nextOffer.offerStatus === 'proposed',
                                            'text-green-500': nextOffer.offerStatus !== 'proposed',
                                        }"
                                    >
                                        {{ $t('organizations.offerStatus.' + nextOffer.offerStatus) }}
                                    </span>
                                </span>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td class="p-1 border text-center">
                            <table>
                                <tr>
                                    <th class="p-1">
                                        <div class="flex w-full justify-end">
                                            <span class="font-bold">{{ $t('organizations.creditAmount') }} :</span>
                                        </div>
                                    </th>
                                    <td class="p-1">
                                        <div class="flex w-full justify-start">{{ nextOffer.creditAmount }}</div>
                                    </td>
                                </tr>
                                <tr>
                                    <th class="p-1">
                                        <div class="flex w-full justify-end">
                                            <span class="font-bold">{{ $t('organizations.paymentModality') }} :</span>
                                        </div>
                                    </th>
                                    <td class="p-1">
                                        <div class="flex w-full justify-start">
                                            {{ $t('organizations.frequencies.' + nextOffer.billingFrequency) }}
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <th class="p-1"></th>
                                    <td class="p-1">
                                        <div class="flex w-full justify-start">
                                            <span v-if="nextOffer.billingFrequency === 'annual'">
                                                {{ $t('organizations.paymentDate') }}
                                                {{ nextOffer.firstPaymentAt | humanizeDate }} :
                                                {{ $n(nextOffer.amountHT + nextOffer.amountHT * 0.2, currency) }}
                                            </span>
                                            <span v-else-if="nextOffer.billingFrequency === 'monthly'">
                                                {{ $t('organizations.firstPayment') }}
                                                {{ nextOffer.firstPaymentAt | humanizeDate }} :
                                                {{ $n((nextOffer.amountHT + nextOffer.amountHT * 0.2) / 12, currency) }}
                                            </span>
                                            <span v-else>
                                                {{ $t('organizations.firstPayment') }}
                                                {{ nextOffer.firstPaymentAt | humanizeDate }} :
                                                {{ $n((nextOffer.amountHT + nextOffer.amountHT * 0.2) / 2, currency) }}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                    <tr>
                        <td colspan="2" class="p-2 border" style="vertical-align: top">
                            <span class="text-lg font-bold w-full">Documents</span>
                            <app-file-link file-name="proposition.pdf"></app-file-link>
                            <app-file-link file-name="mandat SEPA.pdf"></app-file-link>
                        </td>
                    </tr>
                    <tr>
                        <td colspan="2">
                            <div class="w-full flex justify-center py-3">
                                <app-button
                                    @click="sendCommercialMail()"
                                    variant="primary"
                                    :label="$t('organizations.contactCommercial')"
                                ></app-button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </template>
        <template v-else>
            <div class="w-full flex justify-center py-3">
                <app-button
                    @click="sendCommercialMail()"
                    variant="primary"
                    :label="$t('organizations.contactCommercial')"
                ></app-button>
            </div>
        </template>
    </div>
</template>

<script>
import AppInputText from '@/components/appInputText/AppInputText.vue';
import { getOrganization } from '@/features/organizations/organizations.service';
import AppPhone from '@/components/appPhone/AppPhone.vue';
import { getLoggedUser } from '@/features/tokens/token.service';
import AppPhoneInput from '@/components/appPhoneInput/AppPhoneInput.vue';
import AppMail from '@/components/appMail/AppMail.vue';
import AppButton from '@/components/appButton/AppButton.vue';
import AppPopup from '@/components/app-popup/AppPopup.vue';
import AppFooter from '@/components/appFooter/AppFooter.vue';
import { updateBreadCrumbs } from '@/state/state';
import AppSeparator from '@/components/appSeparator/AppSeparator.vue';
import AppFileLink from '@/components/appFileLink/AppFileLink.vue';

export default {
    components: {
        AppFileLink,
        AppSeparator,
        AppFooter,
        AppPhoneInput,
        AppInputText,
        AppPopup,
        AppMail,
        AppPhone,
        AppButton,
    },
    created() {
        this.init();
    },
    watch: {
        $route() {
            this.init();
        },
    },
    computed: {
        nextLevy() {
            return this.currentSubscription.levies.find((levy) => levy.date.getTime() > Date.now());
        },
        currentSubscription() {
            const subscription = this.organization.subscriptions.find(
                (s) => s.validatedAt && s.startDate <= Date.now() && s.endDate > Date.now(),
            );
            if (subscription) {
                subscription.remainingCredits = 10; // TODO make it dynamic
            }
            return subscription;
        },
        nextOffer() {
            const subscription = this.organization.subscriptions.find(
                (s) => !s.validatedAt && s.proposedAt < Date.now() && s.startDate > Date.now(),
            );
            if (subscription) {
                if (subscription.proposedAt && !subscription.signedAt && !subscription.validatedAt) {
                    subscription.offerStatus = 'proposed';
                } else if (subscription.proposedAt && subscription.signedAt && !subscription.validatedAt) {
                    subscription.offerStatus = 'signedAt';
                } else if (subscription.proposedAt && subscription.signedAt && subscription.validatedAt) {
                    subscription.offerStatus = 'validated';
                }
            }
            return subscription;
        },
    },
    methods: {
        async init() {
            this.organization = await getOrganization(this.$route.params.organizationId);
            updateBreadCrumbs({ organizationName: this.organization.name });
            const token = getLoggedUser();
            const isRoot = token && token.roles.includes('root');
            const isOwner = this.organization.ownerId === token.id;
            this.readOnly = !isRoot && !isOwner;
        },
        sendProMail() {
            window.location.href =
                "mailto:info@tex.moe?subject=Pourrions%20nous%20étudier%20mes%20besoins%20pour%20l'offre%20pro%20?&body=Bonjour,%0D%0A%0D%0AJ'aimerais passer à l'offre Pro, pourrais-je en savoir plus ?%0D%0A%0D%0AMerci";
        },
        sendCommercialMail() {
            window.location.href =
                'mailto:info@tex.moe?subject=' +
                (this.nextOffer?.code ? `[${this.nextOffer.code}]%20` : '') +
                "Pourrions%20échanger%20à%20propos%20de%20mon%20offre%20commerciale%20?&body=Bonjour,%0D%0A%0D%0AJ'aimerais échanger sur mon offre...%0D%0A%0D%0AMerci";
        },
        animateCTA() {
            this.buttonCTAClasses = 'animate-bounce';
            setTimeout(() => (this.buttonCTAClasses = ''), 3000);
        },
    },
    data() {
        return {
            readOnly: true,
            organization: { owner: {}, subscriptions: [] },
            integerFormat: {
                decimal: {
                    style: 'decimal',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                },
            },
            buttonCTAClasses: '',
            currency: {
                currency: 'EUR',
                style: 'currency',
            },
        };
    },
};
</script>
